import { GameModel } from "@westpierstudio/luma/game";
import { reset, Resettable } from "@westpierstudio/luma/utils/reset";
import { SpinModeType, SpinSpeedType } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/UIStateMachine";
import { ReelsModel } from "components/reels/ReelsModel";
import { WinlinesModel } from "components/winlines/WinlinesModel";
import { MultiplierPanelsModel } from "components/multiplierpanels/MultiplierPanelsModel";
import { FREE_ROUND, OpenGameData, OpenGameEvent, VariantName } from "servertypes/gameevents/OpenGame";
import { InitialSpinEvent } from "servertypes/gameevents/InitialSpin";
import { InitialWheelEvent } from "servertypes/gameevents/Wheel";
import { FreeSpinEvent } from "servertypes/gameevents/FreeSpin";
import { FreeSpinWheelEvent } from "servertypes/gameevents/FreeSpinWheel";
import { CoreSymbolID, CoreSymbolNumberMap, MultiplierCountersMap, Multipliers, PayLineWin, ReelSetSymbols, SymbolID } from "servertypes/SharedTypes";
import { GameRequest } from "servertypes/ServerEvents";
import { WheelModel } from "components/wheel/WheelModel";
import { WheelPrizePanelModel } from "components/wheelprizepanel/WheelPrizePanelModel";
import { getWinType, isBigWinType, WinType } from "@westpierstudio/core-module-oryx/utils/win-countup";
import { times } from "lodash";
import { devLog } from "utils/Misc";

export enum GameState {
    INIT = `INIT`,
    IDLE = `IDLE`,
    SPIN = `SPIN`,
    SHOW_WINS = `SHOW_WINS`,
    SHOW_BIG_WIN = `SHOW_BIG_WIN`,
    WHEEL_IDLE = `WHEEL_IDLE`,
    WHEEL_ACTIVE = `WHEEL_ACTIVE`,
    WHEEL_COLLECT_PRIZE = `WHEEL_COLLECT_PRIZE`,
    FREE_SPIN = `FREE_SPIN`,
    FREE_SPIN_SHOW_WINS = `FREE_SPIN_SHOW_WINS`,
    FREE_SPIN_SHOW_BIG_WIN = `FREE_SPIN_SHOW_BIG_WIN`,
    FREE_SPIN_WHEEL_IDLE = `FREE_SPIN_WHEEL_IDLE`,
    FREE_SPIN_WHEEL_ACTIVE = `FREE_SPIN_WHEEL_ACTIVE`,
    FREE_SPIN_WHEEL_COLLECT_PRIZE = `FREE_SPIN_WHEEL_COLLECT_PRIZE`,
    FREE_SPIN_END = `FREE_SPIN_END`
}

export enum PrecogState {
    NONE = `NONE`,
    GREAT_WIN = `GREAT_WIN`,
    BIG_WIN = `BIG_WIN`,
    MASSIVE_WIN = `MASSIVE_WIN`,
    COLOSSAL_WIN = `COLOSSAL_WIN`,
    BONUS = `BONUS`
}

export class LuckyWealthCatGameModel extends GameModel {
    private resetList: Resettable[] = [];
    private _openGameEvent?: OpenGameEvent;
    private _state = GameState.INIT;
    private _precogState = PrecogState.NONE;
    reels!: ReelsModel;
    winlines!: WinlinesModel;
    multiplierPanels!: MultiplierPanelsModel;
    wheel!: WheelModel;
    wheelPrizePanel!: WheelPrizePanelModel;
    activeMultipliers: Partial<CoreSymbolNumberMap> = {};
    multiplierCountersState: MultiplierCountersMap = {};
    orderedSymbolsToCollect: {id: CoreSymbolID, lowestRow: number}[] = [];
    winningSymbolState: boolean[][] = [];
    playLandAnim: boolean[][] = [];
    playBonusLandSound = [false, false, false];
    multiplierBadgeWinData: {index: number, value: number}[] = [];
    spinSpeedType = SpinSpeedType.REGULAR;
    spinModeType = SpinModeType.MANUAL;
    winningSymbolSoundID?: SymbolID;
    precogTease = false;
    preBonusWheel = false;
    bigWin = false;
    winLineCount = 30;

    get openGameData() {
        return this._openGameEvent?.body.data as OpenGameData;
    }

    get anticipation(): boolean {
        return this._precogState !== PrecogState.NONE;
    }

    get shouldPrecogCoinShower(): boolean {
        return [PrecogState.BONUS, PrecogState.COLOSSAL_WIN, PrecogState.MASSIVE_WIN, PrecogState.BIG_WIN].includes(this._precogState);
    }

    get state() {
        return this._state;
    }

    set state(state: GameState) {

        devLog(`New State: ${state}`);
        this._state = state;
    }

    get inFreeSpinBonus(): boolean {
        return (
            this._state === GameState.FREE_SPIN ||
            this._state === GameState.FREE_SPIN_SHOW_WINS ||
            this._state === GameState.FREE_SPIN_SHOW_BIG_WIN ||
            this._state === GameState.FREE_SPIN_WHEEL_IDLE ||
            this._state === GameState.FREE_SPIN_WHEEL_ACTIVE ||
            this._state === GameState.FREE_SPIN_WHEEL_COLLECT_PRIZE
        );
    }

    getSpinType(freeSpin: boolean) {
        return this.reels.getSpinType(freeSpin, this.spinSpeedType);
    }

    override setup(): void {
        super.setup();

        this.resetList.push(this.reels = new ReelsModel(this.services));
        this.resetList.push(this.winlines = new WinlinesModel(this.services));
        this.resetList.push(this.multiplierPanels = new MultiplierPanelsModel(this.services));
        this.resetList.push(this.wheel = new WheelModel(this.services));
        this.resetList.push(this.wheelPrizePanel = new WheelPrizePanelModel(this.services));
    }

    processOpenGameEvent(event: OpenGameEvent): void {

        this._openGameEvent = event;

        const {data, lastEvent} = event.body;

        let currentCoinSize;

        if (lastEvent) {
            currentCoinSize = lastEvent.data.betData.coinSize;
            this.multiplierCountersState = lastEvent.data.finalPersistingState.multiplierCountersMap;

        } else {
            currentCoinSize = data.betConfig.defaultCoinSize;
            this.multiplierCountersState = data.roundInitialPersistingState.multiplierCountersMap;
        }

        this.wheel.processWedgeData(data.wedgesConfig, data.wedgesSequence, currentCoinSize);

        const {variantName, paylinesRows} = event.body.data;
        this.winLineCount = paylinesRows.length; // can use the lines array directly for line count if the variation data is missing.
        this.winLineCount = this.extractVariantWinLineCount(variantName);
    }

    extractVariantWinLineCount(variant?: VariantName) {
        if (variant === undefined) {
            console.warn(`No variantName data in openGame event - falling back to default lineCount of ${this.winLineCount} lines`);
            return this.winLineCount;
        } else {
            const matches = variant.match(/lines(\d+)\./);
            if (matches === null) {
                console.warn(`No line count identified in the variantName - falling back to default lineCount of ${this.winLineCount} lines`);
                return this.winLineCount;
            }
            console.log(`${matches[1]} lines variant`);
            return parseInt(matches[1]);
        }
    }

    processSpinEvent(event: InitialSpinEvent | FreeSpinEvent, symbolMultiplierSet: CoreSymbolNumberMap): void {

        this.reels.spinResult = event.body.data.gameplay.reels;

        const {betData, gameplay, nextActions, totalMoneyWon} = event.body.data;
        const bonus = (nextActions !== undefined) && (nextActions[0] === GameRequest.WHEEL_SPIN);
        this.preBonusWheel = bonus;

        const winType = getWinType(betData.playerBet, totalMoneyWon);
        this.bigWin = isBigWinType(winType);

        this.calculatePrecogState(bonus, winType);
        this.processMultiplierData(event, symbolMultiplierSet);

        this.setWinningSymbolState(gameplay.paylineWins);
        this.setWinningSymbolSoundID(gameplay.paylineWins, gameplay.reels);
        this.setLandAnimState(gameplay.reels);
        this.setBonusLandSoundState(this.playLandAnim);
        this.setMultiplierBadgeWinData(gameplay.reels);

        this.winlines.storeWins(event.body.data.gameplay.paylineWins, event.body.data.gameplay.multipliedWinsMap);
    }

    processWheelEvent(event: InitialWheelEvent | FreeSpinWheelEvent): void {

        const {betData, gameplay, totalMoneyWon} = event.body.data;

        const winType = getWinType(betData.playerBet, totalMoneyWon);
        this.bigWin = isBigWinType(winType);

        this.wheel.processWinningWedge(gameplay.winningWedge);

        this.multiplierCountersState = event.body.data.finalPersistingState.multiplierCountersMap;
    }

    calculateActiveMultipliers(multipliers: CoreSymbolNumberMap, state?: Partial<CoreSymbolNumberMap>) {
        if (!state) {
            return {};
        }

        const activeMultipliers:Partial<CoreSymbolNumberMap> = {};

        for (const [key, gamesRemaining] of Object.entries(state)) {
            activeMultipliers[key as keyof CoreSymbolNumberMap] = multipliers[key as keyof CoreSymbolNumberMap];
        }

        return activeMultipliers;
    }

    retrieveCurrentCoinSize(openGameEvent: OpenGameEvent) {
        const lastEvent = openGameEvent.body.lastEvent;
        const betConfig = openGameEvent.body.data.betConfig;

        return (lastEvent?.data.betData !== undefined)
            ? lastEvent.data.betData.coinSize
            : betConfig.defaultCoinSize;
    }

    updateActiveMultipliers(symbolMultiplierSet: CoreSymbolNumberMap): void {

        for (const key of Object.keys(this.activeMultipliers)) {
            this.activeMultipliers[key as CoreSymbolID] = symbolMultiplierSet[key as CoreSymbolID];
        }
    }

    override reset(): void {
        super.reset();
        reset(this.resetList);

        this._openGameEvent = undefined;
        this._precogState = PrecogState.NONE;

        this.state = GameState.INIT;
        this.activeMultipliers = {};
        this.multiplierCountersState = {};
        this.orderedSymbolsToCollect = [];
        this.winningSymbolState = [];
        this.playLandAnim = [];
        this.playBonusLandSound = [false, false, false];
        this.multiplierBadgeWinData = [];
        this.spinSpeedType = SpinSpeedType.REGULAR;
        this.spinModeType = SpinModeType.MANUAL;
        this.winningSymbolSoundID = undefined;
        this.precogTease = false;
        this.preBonusWheel = false;
        this.bigWin = false;
    }

    private calculatePrecogState(enteringBonusWheel: boolean, winType: WinType): void {

        this._precogState = PrecogState.NONE;

        let precogEligible = false;

        if (enteringBonusWheel) {

            precogEligible = true;

            if (Math.random() <= 0.4) {
                this._precogState = PrecogState.BONUS;
            }
        } else if (isBigWinType(winType)) {

            precogEligible = true;

            switch(winType) {
                case WinType.GREAT_WIN:
                    if (Math.random() <= 0.3) {
                        this._precogState = PrecogState.GREAT_WIN;
                    }
                    break;
                case WinType.BIG_WIN:
                    if (Math.random() <= 0.4) {
                        this._precogState = PrecogState.BIG_WIN;
                    }
                    break;
                case WinType.MASSIVE_WIN:
                    if (Math.random() <= 0.5) {
                        this._precogState = PrecogState.MASSIVE_WIN;
                    }
                    break;
                case WinType.COLOSSAL_WIN:
                    if (Math.random() <= 0.6) {
                        this._precogState = PrecogState.COLOSSAL_WIN;
                    }
                    break;
                default:
                    break;
            }
        }

        if (precogEligible && this._precogState === PrecogState.NONE && Math.random() <= 0.03) {
            this.precogTease = true;
        } else {
            this.precogTease = false;
        }
    }

    private setWinningSymbolState(paylineWins: PayLineWin[]): void {

        this.winningSymbolState = times(5, () => [false,false,false]);
        this.multiplierBadgeWinData = [];

        for (const win of paylineWins) {
            for (const entry of win.pattern) {
                this.winningSymbolState[entry.column][entry.row] = true;
            }
        }
    }

    private setWinningSymbolSoundID(paylineWins: PayLineWin[], reels: ReelSetSymbols): void {

        this.winningSymbolSoundID = undefined;

        if (paylineWins.length > 0) {

            const highestWin = paylineWins[0];
            const symbolIDs = highestWin.pattern.map((item) => reels[item.column][item.row]);
            const anyWilds = symbolIDs.some((item) => item === `WW`); //"B1W" ??

            if (anyWilds) {
                this.winningSymbolSoundID = `WW`;

            } else if ( [`M1`, `M2`, `M3`].includes(highestWin.symbol)) {
                this.winningSymbolSoundID = highestWin.symbol;
            }
        }
    }

    /*
        B1 / B1W:
            Always play on 1st + 3rd reels
            Only play on 2nd reel if symbol also on 1st reel
    */
    private setLandAnimState(reels: ReelSetSymbols): void {

        this.playLandAnim = times(5, () => [false,false,false]);

        const isTrue = (entry: boolean) => entry === true;

        let symbolIndex;
        for (const [i, reel] of reels.entries()) {

            symbolIndex = reel.indexOf(`B1`);

            if (symbolIndex !== -1) {

                if (i === 1) {
                    this.playLandAnim[i][symbolIndex] = this.playLandAnim[0].some(isTrue) ? true : false;
                } else {
                    this.playLandAnim[i][symbolIndex] = true;
                }
            }
        }
    }

    private setBonusLandSoundState(playLandAnim: boolean[][]): void {

        const isTrue = (entry: boolean) => entry === true;

        let canTriggerBonus = true;

        for (const i of this.playBonusLandSound.keys()) {

            canTriggerBonus = playLandAnim[i].some(isTrue) && canTriggerBonus;
            this.playBonusLandSound[i] = canTriggerBonus;
        }
    }

    private setMultiplierBadgeWinData(reels: ReelSetSymbols): void {

        if (this.activeMultipliers) {

            const firstReelState = this.winningSymbolState[0];
            let symbolID, multiplier;

            for (const [i, entry] of firstReelState.entries()) {

                symbolID = reels[0][i] as CoreSymbolID;
                multiplier = this.activeMultipliers[symbolID];

                if (entry && multiplier) {
                    this.multiplierBadgeWinData.push({index: i, value: multiplier});
                }
            }
        }
    }

    private processMultiplierData(event: InitialSpinEvent | FreeSpinEvent, symbolMultiplierSet: CoreSymbolNumberMap): void {

        this.multiplierCountersState = event.body.data.finalPersistingState.multiplierCountersMap;

        const {multipliers, paylineWins} = event.body.data.gameplay;

        this.activeMultipliers = {};
        let symbolID;

        for (const item of Object.keys(multipliers.initialCounters)) {

            symbolID = item as CoreSymbolID;

            if (!multipliers.removedSymbols.includes(symbolID)) {
                this.activeMultipliers[symbolID] = symbolMultiplierSet[item as CoreSymbolID];
            }
        }

        this.generateOrderedSymbolsToCollect(multipliers,paylineWins);
    }

    /*
        To collect symbols top to bottom as described in the content key + seen in ref video.

        For each new symbol loop through the paylines and find the lowest row which contains a win for it
        Store out in sorted order
    */
    private generateOrderedSymbolsToCollect(multipliers: Multipliers, paylineWins: PayLineWin[]): void {

        this.orderedSymbolsToCollect = [];

        if (multipliers.newSymbols.length > 0) {

            for (const symbol of multipliers.newSymbols) {

                let lowestRow = Infinity;

                for (const payline of paylineWins) {

                    if (payline.symbol === symbol && payline.pattern[0].row < lowestRow) {
                        lowestRow = payline.pattern[0].row;
                    }
                }

                if (this.orderedSymbolsToCollect.length > 0) {

                    let addIndex = 0;

                    for (const item of this.orderedSymbolsToCollect) {

                        if (item.lowestRow > lowestRow) {
                            break;
                        }

                        addIndex++;
                    }

                    this.orderedSymbolsToCollect.splice(addIndex, 0, {id: symbol, lowestRow});

                } else {
                    this.orderedSymbolsToCollect.push({id: symbol, lowestRow});
                }
            }
        }
    }

    get isFreeRound() {
        return this._freeRoundData !== undefined;
    }

    get activeFreeRoundPending() {
        return this._freeRoundData && this._freeRoundData.status === `PENDING`;
    }

    get activeFreeRoundsFinished() {
        return this._freeRoundData && this._freeRoundData.status === `FINISHED`;
    }

    get activeFreeRoundData() {
        if (!this._freeRoundData) {
            throw new Error(`Missing main model freeRoundData`);
        }
        return this._freeRoundData;
    }

    private _freeRoundData?: FREE_ROUND;

    setFreeRoundData(data: FREE_ROUND) {
        this._freeRoundData = data;
    }

    freeRoundsComplete() {
        this._freeRoundData = undefined;
    }
}